export interface Point {
  x: number;
  y: number;
}

export interface Dimensions {
  width: number;
  height: number;
}

export interface Rectangle {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface BoundingBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
}

export interface CornerPositions {
  topLeft: Point;
  topRight: Point;
  bottomLeft: Point;
  bottomRight: Point;
}

/**
 * Geometry Helper.
 *
 * Provides utility functions for geometric calculations, coordinate transformations,
 * and bounding box operations commonly used in UI components.
 */
export class GeometryHelper {
  /**
   * Calculate bounding box from an array of points.
   *
   * @param points - Array of points to calculate bounding box from.
   * @returns Bounding box dimensions and position.
   */
  public static getBoundingBoxFromPoints(points: Point[]): BoundingBox {
    if (points.length === 0) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 };
    }

    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return {
      minX,
      maxX,
      minY,
      maxY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * Calculate resize dimensions based on corner and delta.
   *
   * @param startRect - Starting rectangle.
   * @param cornerId - Corner being dragged.
   * @param deltaX - X delta from start position.
   * @param deltaY - Y delta from start position.
   * @param aspectRatio - Optional aspect ratio to maintain.
   * @returns New rectangle dimensions.
   */
  public static calculateResizeDimensions(
    startRect: Rectangle,
    cornerId: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight',
    deltaX: number,
    deltaY: number,
    aspectRatio?: number
  ): Rectangle {
    let left = startRect.left;
    let top = startRect.top;
    let width = startRect.width;
    let height = startRect.height;

    // Calculate new dimensions based on corner
    switch (cornerId) {
      case 'bottomRight':
        width = startRect.width + deltaX;
        height = startRect.height + deltaY;
        break;
      case 'topRight':
        width = startRect.width + deltaX;
        height = startRect.height - deltaY;
        top = startRect.top + (startRect.height - height);
        break;
      case 'bottomLeft':
        width = startRect.width - deltaX;
        left = startRect.left + (startRect.width - width);
        height = startRect.height + deltaY;
        break;
      case 'topLeft':
        width = startRect.width - deltaX;
        left = startRect.left + (startRect.width - width);
        height = startRect.height - deltaY;
        top = startRect.top + (startRect.height - height);
        break;
      default:
        // No change for unknown corner
        break;
    }

    // Apply aspect ratio lock if provided
    if (aspectRatio && aspectRatio > 0) {
      const newAspect = width / height;

      if (Math.abs(newAspect - aspectRatio) > Math.abs((height / width) - (1 / aspectRatio))) {
        height = width / aspectRatio;
      } else {
        width = height * aspectRatio;
      }

      // Adjust position based on corner
      switch (cornerId) {
        case 'topRight':
          top = startRect.top + (startRect.height - height);
          break;
        case 'bottomLeft':
          left = startRect.left + (startRect.width - width);
          break;
        case 'topLeft':
          left = startRect.left + (startRect.width - width);
          top = startRect.top + (startRect.height - height);
          break;
        default:
          // No position adjustment needed for other corners
          break;
      }
    }

    return { left, top, width, height };
  }

  /**
   * Apply layout styles to an element from percentage-based bounding box.
   *
   * @param element - HTML element to apply styles to.
   * @param boundingBox - Bounding box with percentage values.
   * @param boundingBox.left - Left percentage value.
   * @param boundingBox.top - Top percentage value.
   * @param boundingBox.width - Width percentage value.
   * @param boundingBox.height - Height percentage value.
   */
  public static applyLayoutFromBoundingBox(element: HTMLElement, boundingBox: { left: number; top: number; width: number; height: number }): void {
    // Apply percentage-based styles directly
    // This allows the browser to automatically maintain proportions when the container resizes
    element.style.left = `${boundingBox.left}%`;
    element.style.top = `${boundingBox.top}%`;
    element.style.width = `${boundingBox.width}%`;
    element.style.height = `${boundingBox.height}%`;
  }

  /**
   * Convert element rectangle to corner positions.
   *
   * @param rect - Element rectangle.
   * @returns Corner positions.
   */
  public static elementRectToCorners(rect: Rectangle): CornerPositions {
    return {
      topLeft: { x: rect.left, y: rect.top },
      topRight: { x: rect.left + rect.width, y: rect.top },
      bottomLeft: { x: rect.left, y: rect.top + rect.height },
      bottomRight: { x: rect.left + rect.width, y: rect.top + rect.height }
    };
  }

  /**
   * Convert corner positions to element rectangle.
   *
   * @param corners - Corner positions.
   * @returns Element rectangle.
   */
  public static cornersToElementRect(corners: CornerPositions): Rectangle {
    const bbox = this.getBoundingBoxFromPoints([
      corners.topLeft,
      corners.topRight,
      corners.bottomLeft,
      corners.bottomRight
    ]);

    return {
      left: bbox.minX,
      top: bbox.minY,
      width: bbox.width,
      height: bbox.height
    };
  }

  /**
   * Convert element rectangle to percentage-based bounding box.
   *
   * @param elementRect - Element rectangle.
   * @param parentRect - Parent container rectangle.
   * @param isAbsolute - Optional boolean flag. If true, ignores parentRect position.
   * @returns Percentage-based bounding box.
   */
  public static elementRectToPercentageBoundingBox(elementRect: Rectangle, parentRect: Rectangle, isAbsolute?: boolean): { left: number; top: number; width: number; height: number } {
    const elementLeft = isAbsolute ? elementRect.left : (elementRect.left - parentRect.left);
    const elementTop = isAbsolute ? elementRect.top : (elementRect.top - parentRect.top);

    return {
      left: (elementLeft / parentRect.width) * 100,
      top: (elementTop / parentRect.height) * 100,
      width: (elementRect.width / parentRect.width) * 100,
      height: (elementRect.height / parentRect.height) * 100
    };
  }
}
